import { Dropdown, Icon } from '@ps/isp-web-component-library';
import { ChevronMediumDown } from '@ps/isp-web-component-library/icons/ChevronMediumDown';
import { ChevronMediumLeft } from '@ps/isp-web-component-library/icons/ChevronMediumLeft';
import type { EventTypeView, EventsQuickSwitcherData } from '@ps/isp-web-services-types';
import { isToday } from 'date-fns';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { CardTitle, DefaultLink, Menu } from '@swf/sports/components';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';
import { DROPDOWN_HEIGHT_ADJUST } from '@swf/sports/utils/constants';

import TodaysEventLink from '../../components/EventSwitcher/components/TodaysEventLink/TodaysEventLink';
import {
  SkeletonEventDividerText,
  SkeletonEventRow
} from '../../components/LoadingState/LoadingState';

import styles from './Events.scss';

interface EventsProps {
  title: React.ReactNode;
  quickSwitcherData: EventsQuickSwitcherData;
}

const Events: React.FC<EventsProps> = ({ title, quickSwitcherData }) => {
  const { events } = quickSwitcherData;
  const { quickSwitcher } = useDispatch<Dispatch>();

  const { competitionId } = useSelector(select.sports.getUrlParams);
  const isLoading = useSelector(select.quickSwitcher.isLoading);

  const handleTitleClick = () => {
    quickSwitcher.quickSwitcherRequested({ hierarchyLevel: 'competition', facetId: competitionId });
    quickSwitcher.navigating(true);
  };

  return (
    <Dropdown
      title={
        <div
          onClick={handleTitleClick}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Icon icon={ChevronMediumLeft} size="sm" className={styles.icon} />
          {quickSwitcherData.title}
        </div>
      }
      verticalOffset="XXS"
      overrideHeightAdjust={DROPDOWN_HEIGHT_ADJUST}
      triggerEl={({ arrowRef, ...props }) => (
        <CardTitle {...props} isDropdown>
          {title}

          <Icon ref={arrowRef} icon={ChevronMediumDown} size="sm" className={styles.icon} />
        </CardTitle>
      )}
    >
      {quickSwitcherData.items.map((eventGroup) => {
        const [firstEventId] = eventGroup;
        const { eventStartTime } = events[firstEventId];

        return (
          <section key={eventStartTime}>
            <h2 className={styles.dividerText}>
              {!isLoading ? (
                <FormattedDateTime date={eventStartTime} format="long" hideTime />
              ) : (
                <SkeletonEventDividerText />
              )}
            </h2>
            <Menu>
              {eventGroup.map((eventId) => {
                if (isLoading) return <SkeletonEventRow key={eventId} />;

                const {
                  competitionId,
                  eventTypeId,
                  eventSlug,
                  eventName,
                  eventStartTime,
                  isInPlay
                } = events[eventId];
                const { competitionSlug } = quickSwitcherData.competitions[competitionId];
                const { eventTypeSlug } = quickSwitcherData.eventTypes[
                  eventTypeId
                ] as EventTypeView;
                const parsedEventStartTime = new Date(eventStartTime);
                const isEventToday = isToday(parsedEventStartTime);

                const values = {
                  eventId,
                  eventName,
                  eventSlug,
                  competitionId,
                  competitionSlug,
                  eventTypeId,
                  eventTypeSlug
                };
                return (
                  <DefaultLink
                    key={eventId}
                    alias="sportsEvent"
                    values={values}
                    exact
                    activeClassName={styles.activeLink}
                    hasChevron={!isInPlay && !isEventToday}
                  >
                    {isInPlay || isEventToday ? (
                      <TodaysEventLink
                        isInPlay={isInPlay}
                        isEventToday={isEventToday}
                        parsedEventStartTime={parsedEventStartTime}
                        eventName={eventName}
                      />
                    ) : (
                      eventName
                    )}
                  </DefaultLink>
                );
              })}
            </Menu>
          </section>
        );
      })}
    </Dropdown>
  );
};

export default Events;
